import React, { useEffect } from "react";
import { Plus, Image as ImageIcon, List, Layout, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import TagInput from '../portfolio/TagInput';
import ImageUpload from "../common/ImageUpload";

const OverviewForm = ({ data, onChange, submitted }) => {
  // Ensure we always have 4 cards of correct types
  useEffect(() => {
    const fixedTypes = ["image", "image", "data", "imageWithText"];
    let updated = false;
    const newCards = [...(data.cards || [])];

    if (newCards.length !== 4) {
      // If not 4 cards, reset or fill to 4
      while (newCards.length < 4) {
        newCards.push({ type: "", image: "", title: "", sub_title: "", data: [] });
      }
      if (newCards.length > 4) {
        newCards.splice(4);
      }
      updated = true;
    }

    // Ensure types are fixed
    fixedTypes.forEach((type, index) => {
      if (newCards[index].type !== type) {
        newCards[index].type = type;
        updated = true;
      }
    });

    if (updated) {
      onChange(prev => ({ ...prev, cards: newCards }));
    }
  }, [data.cards, onChange]);

  const handleCardChange = (index, field, value) => {
    onChange((prev) => {
      const newCards = [...prev.cards];
      newCards[index] = { ...newCards[index], [field]: value };
      return { ...prev, cards: newCards };
    });
  };

  const handleDataChange = (cardIndex, dataIndex, value) => {
    onChange((prev) => {
      const newCards = [...prev.cards];
      const newData = [...(newCards[cardIndex].data || [])];
      newData[dataIndex] = value;
      newCards[cardIndex] = { ...newCards[cardIndex], data: newData };
      return { ...prev, cards: newCards };
    });
  };

  const addDataItem = (cardIndex) => {
    onChange((prev) => {
      const newCards = [...prev.cards];
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        data: [...(newCards[cardIndex].data || []), ""],
      };
      return { ...prev, cards: newCards };
    });
  };

  const removeDataItem = (cardIndex, dataIndex) => {
    onChange((prev) => {
      const newCards = [...prev.cards];
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        data: newCards[cardIndex].data.filter((_, i) => i !== dataIndex),
      };
      return { ...prev, cards: newCards };
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Overview Description <span className="text-red-500">*</span></Label>
          <textarea
            className={`w-full h-32 px-4 py-2 rounded-xl border focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${(!data.description && submitted) ? "border-red-500" : "border-slate-200"}`}
            value={data.description}
            onChange={(e) => onChange(prev => ({ ...prev, description: e.target.value }))}
            placeholder="General project overview..."
          />
        </div>
        <div className="space-y-2">
          <Label>Overview Tags <span className="text-red-500">*</span></Label>
          <div className={(!data.tags || data.tags.length === 0) && submitted ? "rounded-lg border border-red-500 p-1" : ""}>
            <TagInput
              tags={data.tags || []}
              setTags={(tags) => onChange(prev => ({ ...prev, tags }))}
              placeholder="Add overview tags..."
            />
          </div>
          <p className="text-[10px] text-slate-400">Press Enter or comma to add tags</p>
        </div>
      </div>

      <div className="space-y-4">
        <Label className="text-lg font-bold">Dynamic Cards (Fixed 4 Cards) <span className="text-red-500">*</span></Label>

        <div className="grid grid-cols-1 gap-6">
          {(data.cards || []).map((card, index) => (
            <div
              key={index}
              className={`bg-slate-50 p-6 rounded-2xl border relative group transition-all ${submitted && (
                (card.type === "image" && !card.image) ||
                (card.type === "data" && (!card.title || !card.sub_title || !card.data?.length || card.data.some(d => !d))) ||
                (card.type === "imageWithText" && (!card.image || !card.title))
              ) ? "border-red-500 shadow-sm shadow-red-100" : "border-slate-200"}`}
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <span className="font-semibold text-slate-700 capitalize">
                  {card.type === "image" ? "Single Image" :
                    card.type === "data" ? "Data Points / List" :
                      "Image with Headline"}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {(card.type === "image" || card.type === "imageWithText") && (
                  <div className={(!card.image && submitted) ? "border-2 border-dashed border-red-300 rounded-xl p-1" : ""}>
                    <ImageUpload
                      label={<span>Card Image <span className="font-normal text-xs text-gray-400">{card.type === "image" ? "(1500 × 1125 px)" : "(300 × 150 px)"}</span> <span className="text-red-500">*</span></span>}
                      value={card.image}
                      onChange={(url) => handleCardChange(index, "image", url)}
                    />
                  </div>
                )}

                {(card.type === "data" || card.type === "imageWithText") && (
                  <div className="space-y-2">
                    <Label>Title <span className="text-red-500">*</span></Label>
                    <Input
                      value={card.title}
                      className={(!card.title && submitted) ? "border-red-500" : ""}
                      onChange={(e) =>
                        handleCardChange(index, "title", e.target.value)
                      }
                      placeholder="Card headline"
                    />
                  </div>
                )}

                {card.type === "data" && (
                  <>
                    <div className="space-y-2">
                      <Label>Subtitle <span className="text-red-500">*</span></Label>
                      <Input
                        value={card.sub_title}
                        className={(!card.sub_title && submitted) ? "border-red-500" : ""}
                        onChange={(e) =>
                          handleCardChange(index, "sub_title", e.target.value)
                        }
                        placeholder="Small lead text"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-xs">List Items <span className="text-red-500">*</span></Label>
                        <button
                          type="button"
                          onClick={() => addDataItem(index)}
                          className="text-primary text-[10px] font-bold hover:underline"
                        >
                          + add item
                        </button>
                      </div>
                      <div className="space-y-2">
                        {(!card.data?.length && submitted) && (
                          <p className="text-[10px] text-red-500">Please add at least one list item.</p>
                        )}
                        {card.data?.map((item, di) => (
                          <div key={di} className="flex gap-2 items-center">
                            <Input
                              value={item}
                              onChange={(e) =>
                                handleDataChange(index, di, e.target.value)
                              }
                              placeholder="Bullet point text"
                              className={`h-8 text-sm ${(!item && submitted) ? "border-red-500" : ""}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeDataItem(index, di)}
                              className="text-red-400 hover:text-red-600 p-1"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewForm;
